import { OverlayContentComponent } from '@acaprojects/ngx-widgets';
import { Component } from '@angular/core';
import { ADynamicFormField } from '@acaprojects/ngx-dynamic-forms';

import { IEvent, ISpace, ICateringOrderList, IUser } from '../../services/data/models/interfaces';

import { generateBookingFormMetadata } from '../../shared/utilities/booking.utilities';
import { formatSpaces } from '../../shared/utilities/formatting.utilities';

import * as dayjs from 'dayjs';

export interface IBookingFlowEvent {
    type: 'next' | 'previous';
    data?: { [name: string]: any };
}

@Component({
    selector: 'booking-modal',
    templateUrl: './booking-modal.template.html',
    styleUrls: ['./booking-modal.styles.scss'],
})
export class BookingModalComponent extends OverlayContentComponent {
    /** Currently displayed page of the booking modal flow */
    public page: 'form' | 'space' | 'equipment' | 'catering' | 'catering-details' | 'details';
    /** Booking ID */
    public id: string;
    /** List of form fields associated with a booking */
    public form_fields: ADynamicFormField[];
    /** Form field for the selected date */
    public date_field: ADynamicFormField;
    /** Time of the last booking event */
    public book: number;
    /** Whether booking succeeded */
    public success: boolean;

    public init(): void {
        if (!this.service.ready()) {
            return this.timeout('init', () => this.init());
        }
        this.clearForm();
    }

    public get form() {
        return this.formToBooking();
    }

    /** Form field for the list of selected spaces */
    public get space_list(): ADynamicFormField {
        const index = this.form_fields.findIndex(i => i.key === 'room');
        return index ? this.form_fields[index]: null;
    }

    /** Form field for listing selected catering items */
    public get catering() {
        return this.form_fields.find(i => i.key === 'catering');
    }

    /** Form field for listing number of expected attendees for each space */
    public get expected_attendees() {
        return this.form_fields.find(i => i.key === 'expected_attendees');
    }

    /** Form field for equipment notes */
    public get notes() {
        return this.form_fields.find(i => i.key === 'equipment');
    }

    /** Form field for catering notes */
    public get catering_notes() {
        return this.form_fields.find(i => i.key === 'catering_notes');
    }

    /** Form field for catering cost code */
    public get catering_code() {
        return this.form_fields.find(i => i.key === 'catering_code');
    }

    /** Form field for equipment cost code */
    public get cost_code() {
        return this.form_fields.find(i => i.key === 'equipment_code');
    }

    /** Whether the form is valid */
    public get valid(): boolean {
        return this.form_fields.reduce((a, v) => {
            const valid = v.children && v.children.length > 0
                ? v.children.reduce((r, i) => r && (i.isValid() || !i.required || i.disabled), true)
                : (v.isValid() || !v.required || v.disabled);
            return a && valid;
        }, true);
    }

    /** List of selected rooms for the booking */
    public get spaces(): ISpace[] {
        const field = this.space_list;
        if (field) {
            return field.control.value || [];
        }
        return [];
    }

    /** Datetime selected for the booking */
    public get date(): number {
        const field = this.form_fields.find(i => i.key === 'date');
        if (field) {
            return field.control.value;
        }
        return dayjs()
            .startOf('m')
            .valueOf();
    }

    /** Selected duration for the booking */
    public get duration(): number {
        let field: any = this.form_fields.find(i => i.key === 'duration');
        if (field) {
            return field.control.value;
        }
        field = this.form_fields.find(i => i.key === 'time_group');
        if (field) {
            return field.control.controls.duration.value;
        }
        return 60;
    }

    /** Number of items in the catering order */
    public get has_ordered(): number {
        const empty = { control: { value: {} } };
        const order: ICateringOrderList = (this.form_fields.find(i => i.key === 'catering') || empty).control.value;
        return order ? Object.keys(order).reduce((v, key) => {
            const room_order = order[key];
            if (room_order && room_order.items) {
                v += room_order.items.reduce((c, i) => c + (i.amount || 0), 0);
            }
            return v;
        }, 0): 0;
    }

    /** Type of booking */
    public get type(): string {
        const empty = { control: { value: {} } };
        const type = ((this.form_fields || []).find(i => i.key === 'booking_type') || empty).control.value || '';
        return typeof type === 'string' ? type : type.id;
    }

    /**
     * Progress to the next step in the flow
     */
    public progress(event: IBookingFlowEvent) {
        const empty = { control: { value: true } };
        const catering = (this.form_fields.find(i => i.key === 'needs_catering') || empty).control.value;
        const space = (this.form_fields.find(i => i.key === 'needs_space') || empty).control.value;
        const id = (this.form_fields.find(i => i.key === 'id') || empty).control.value;
        this.id = id || !space ? '10' : '';
        if (event.type === 'next') {
            switch (this.page) {
                case 'catering':
                    this.page = this.has_ordered > 0 ? 'catering-details' : 'details';
                    break;
                case 'space':
                    if (this.spaces.length > 0) {
                        this.page = 'equipment'
                    } else {
                        this.service.error('No meeting space has been selected');
                    }
                    break;
                case 'equipment':
                    this.page = catering ? 'catering' : 'details';
                    break;
                case 'catering-details':
                    this.page = 'details';
                    break;
                default:
                    this.form_fields.forEach(i => i.control.markAsDirty());
                    if (this.valid) {
                        if (space && !id) {
                            this.page = 'space';
                        } else if (space && catering) {
                            this.page = 'catering';
                        } else {
                            this.page = 'details';
                        }
                    } else {
                        this.service.error('Some of the form fields are invalid');
                    }
                    break;
            }
        } else if (event.type === 'previous') {
            switch (this.page) {
                case 'catering':
                    this.page = (id || !space) ? 'form' : 'space';
                    break;
                case 'equipment':
                    this.page = 'space';
                    break;
                case 'catering-details':
                    this.page = 'catering';
                    break;
                case 'details':
                    this.page = space ? (catering ? (this.has_ordered > 0 ? 'catering-details' : 'catering') : 'equipment') : 'form';
                    break;
                case 'space':
                    this.page = 'form';
                    break;
                default:
                    this.service.navigate(['home']);
                    break;
            }
        } else if (event.type === 'start') {
            this.page = 'form';
        } else if (event.type === 'book') {
            this.book = dayjs().add(1, 'm').startOf('m').valueOf();
        }
    }

    /**
     * Clear any form data
     */
    private clearForm() {
        if (localStorage) {
            localStorage.removeItem('CONCIERGE.booking_form');
        }
        this.loadForm();
    }

    /**
     * Load old form data and intialise the form
     */
    private loadForm() {
        let booking: any = {};
        if (localStorage) {
            booking = JSON.parse(localStorage.getItem('CONCIERGE.booking_form') || '{}');
        }
        this.form_fields = generateBookingFormMetadata(
            booking as IEvent,
            this.service.Settings.get('app.booking.fields'),
            this.service
        );
        this.date_field = this.form_fields
            .reduce((v, i) => v.concat(i.children && i.children.length ? i.children : [i]), [])
            .find(i => i.key === 'date');
        const CUSTOM_FIELDS = ['room', 'catering', 'equipment', 'catering_notes', 'catering_code', 'equipment_code', 'id'];
        const CUSTOM_DEFAULTS = [[], {}, '', '', '', '', ''];
        CUSTOM_FIELDS.forEach((key, i) => {
            this.form_fields.push(new ADynamicFormField({
                key,
                label: key,
                type: 'action',
                format: key === 'room' ? formatSpaces : null,
                hide: true,
                value: booking[key] || CUSTOM_DEFAULTS[i]
            }));
        });
        this.form_fields.forEach(i => {
            if (i.children && i.children.length) {
                i.children.forEach(j => {
                    this.subs.obs[j.key] = j.control.valueChanges.subscribe(v => {
                        if (v && j.control.valid) { this.saveForm(); }
                    });
                });
            } else {
                this.subs.obs[i.key] = i.control.valueChanges.subscribe(v => {
                    if (v && i.control.valid) { this.saveForm(); }
                });
            }
        });
        const empty = { control: { value: true } };
        const id = (this.form_fields.find(i => i.key === 'id') || empty).control.value;
        const time = (this.form_fields.find(i => i.key === 'start') || empty);
        (time as ADynamicFormField).setDisabled(this.duration > 450);
        this.id = id ? '10' : '';
    }

    /**
     * Save changes to the form data
     */
    private saveForm() {
        this.timeout('cancel_close', () => this.event('cancel_close'), 50);
        this.timeout('save_form', () => {
            const form = this.formToBooking();
            if (localStorage) {
                localStorage.setItem('CONCIERGE.booking_form', JSON.stringify(form));
            }
            const empty = { control: { value: true } };
            const time = (this.form_fields.find(i => i.key === 'start') || empty) as ADynamicFormField;
            if (time.disabled !== this.duration > 450) {
                time.setDisabled(!time.disabled);
            }
        });
    }

    /**
     * Convert form field data to a Booking object
     */
    private formToBooking(): IEvent {
        return this.form_fields.reduce((v, i) => {
            if (i.children && i.children.length) {
                i.children.forEach(j => (v[j.key] = j.control.value));
            } else {
                v[i.key] = i.control.value;
            }
            return v;
        }, {}) as IEvent;
    }

    /**
     * Get list of users available to select as the meeting host
     */
    private getHostOptions(): IUser[] {
        const user = this.service.Users.current();
        const list: IUser[] = [user];
        if (user.delegates) {
            for (const email of user.delegates) {
                const delegate = this.service.Users.item(email);
                if (delegate) {
                    list.push(delegate);
                } else {
                    this.timeout(`load_user|${email}`, () =>
                        this.service.Users.show(email, { update: true }).then(() => {
                            this.timeout('update_host_options', () => {
                                const host = this.form_fields.find(i => i.key === 'host');
                                if (host) {
                                    host.metadata = { service: this.service.Bookings, options: this.getHostOptions() };
                                    host.setValue(host.getValue());
                                }
                            });
                        })
                    );
                }
            }
        }
        return list;
    }

    public timedClose() {
        this.timeout('close', () => this.close());
    }

    public cancelClose() {
        this.timeout(
            'cancel-close',
            () => this.clearTimer('close'),
            10
        );
    }
}
